/**
 * Integrations — a gallery of the outside services the workspace connects
 * to, grouped by what they do rather than by vendor, and drilled into like
 * Documents & emails: the section is a short list of destinations, each
 * opening its own space with a back link.
 *
 * Accounting & payments holds the Xero connection (status + action, with the
 * account codes as a dependent panel while connected). Calendar hands over
 * the feed URL. Data holds the whole-Company export (#374) — the connection
 * to whatever system the Company keeps its data in next, which is the same
 * kind of thing as the others and not a sidebar destination of its own.
 *
 * API keys is the credential intake presents to write quotes in here
 * (ADR-0016) — the same shape of thing as pasting a Current RMS key, which
 * is the point: the HireOS RMS is one Backend among several and is now
 * connected the way the others are.
 *
 * Communications was a fourth destination here: the Company's call-in number,
 * its greeting, its forwarding, and the after-hours receptionist (#306). The
 * number and everything about how it behaves belong to `hireos-intake` after
 * the fork (ADR-0016), along with the `company_phone_numbers` table this read
 * — so the row is gone rather than disabled. Intake has its own workspace and
 * its own sign-in for that, reached directly rather than proxied through here.
 *
 * Failures render on the row that caused them (DESIGN-SYSTEM.md error placement);
 * the page-top banner is reserved for the status load itself.
 */
import { useEffect, useState } from "react";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { CopyIcon } from "../../components/ui/icons";
import { useSuccessToast } from "../../components/ui/Toast";
import forms from "../../components/ui/field.module.css";
import { cx } from "@shared/cx";
import { SettingRow } from "../../components/ui/SettingRow";
import {
  disconnectXero,
  getXeroStatus,
  saveXeroSettings,
  startXeroConnect,
  type XeroConnectionStatus,
} from "../../lib/xero-api";
import { getCalendarFeed } from "../../lib/calendar-feed-api";
import { errorMessage } from "../../lib/use-mutation-runner";
import { useSettingCommit } from "../../lib/use-setting-commit";
import {
  BackLink,
  DrillDownRow,
  Loading,
  SectionHeader,
  SettingsGroup,
} from "./section-chrome";
import { InlineBanner } from "../../components/ui/InlineBanner";
import { ApiKeysGroup } from "./ApiKeysGroup";
import { DataSection } from "./DataSection";
import badge from "../../lib/status-badge.module.css";
import chrome from "./settings.module.css";
import styles from "./IntegrationsSection.module.css";

interface AccountCodes {
  revenue: string;
  payment: string;
}

const codesEqual = (a: AccountCodes, b: AccountCodes): boolean =>
  a.revenue === b.revenue && a.payment === b.payment;

const BACK_LABEL = "All integrations";

/** Which drilled-into space is open; null is the gallery. */
type IntegrationView = "accounting" | "calendar" | "api-keys" | "data" | null;

export function IntegrationsSection() {
  const [view, setView] = useState<IntegrationView>(null);

  if (view === "accounting") {
    return (
      <section className={chrome.section}>
        <BackLink label={BACK_LABEL} onClick={() => setView(null)} />
        <SectionHeader
          title="Accounting & payments"
          description="Invoices you send are pushed to your accounting system, and payments reconciled there mark the booking paid here."
        />
        <XeroGroup />
      </section>
    );
  }

  if (view === "calendar") {
    return (
      <section className={chrome.section}>
        <BackLink label={BACK_LABEL} onClick={() => setView(null)} />
        <SectionHeader
          title="Calendar"
          description="Your schedule in the calendar you already live in."
        />
        <CalendarFeedGroup />
      </section>
    );
  }

  if (view === "api-keys") {
    return (
      <section className={chrome.section}>
        <BackLink label={BACK_LABEL} onClick={() => setView(null)} />
        {/* No header here: the group carries its own, and the same words
            twice is what this space read as before. */}
        <ApiKeysGroup />
      </section>
    );
  }

  if (view === "data") {
    return (
      <section className={chrome.section}>
        <BackLink label={BACK_LABEL} onClick={() => setView(null)} />
        <SectionHeader
          title="Data"
          description="Take your data out of HireOS whenever you want."
        />
        <DataSection />
      </section>
    );
  }

  return (
    <section className={chrome.section}>
      <SectionHeader
        title="Integrations"
        description="The outside services your workspace connects to. Open one to change it."
      />
      <ul className={chrome.listBordered}>
        <DrillDownRow
          name="Accounting & payments"
          description="Xero — push invoices out, pull reconciled payments back."
          onOpen={() => setView("accounting")}
        />
        <DrillDownRow
          name="Calendar"
          description="Subscribe once — your bookings stay in sync with your calendar."
          onOpen={() => setView("calendar")}
        />
        <DrillDownRow
          name="API keys"
          description="Let HireOS intake write quotes into this workspace."
          onOpen={() => setView("api-keys")}
        />
        <DrillDownRow
          name="Data"
          description="Export your equipment, customers, crew, jobs, invoices and sent documents as one zip."
          onOpen={() => setView("data")}
        />
      </ul>
    </section>
  );
}

function CalendarFeedGroup() {
  const [feedUrl, setFeedUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const toast = useSuccessToast();

  useEffect(() => {
    const ac = new AbortController();
    getCalendarFeed(ac.signal)
      .then((res) => {
        setFeedUrl(res.url);
        setLoadError(null);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setLoadError(errorMessage(err));
      });
    return () => ac.abort();
  }, []);

  // The success is a toast, not a tick on the button (DESIGN-SYSTEM.md,
  // Toasts).
  const copy = () => {
    if (!feedUrl) return;
    void navigator.clipboard
      .writeText(feedUrl)
      .then(() => toast("Calendar link copied."));
  };

  if (!feedUrl) {
    return (
      <SettingsGroup id="integrations-calendar" title="Calendar feed">
        {loadError ? <Banner variant="error">{loadError}</Banner> : <Loading />}
      </SettingsGroup>
    );
  }

  return (
    <SettingsGroup id="integrations-calendar" title="Calendar feed">
      <div className={styles.feedBlock}>
        <p className={chrome.rowTitle}>Subscription link</p>
        <div className={styles.feedLine}>
          {/* Truncated, not wrapped: the link is a credential to hand to a
              calendar app, never something to read. The full value stays in
              the DOM so the copy control still hands over the whole URL. */}
          <span className={styles.feedUrl}>
            {feedUrl}
          </span>
          <button
            type="button"
            aria-label="Copy calendar subscription link"
            onClick={copy}
            className={chrome.copyButton}
          >
            <CopyIcon className={chrome.icon} />
          </button>
        </div>
        <p className={styles.feedHint}>
          Subscribe to this link in your calendar app. Your bookings appear
          there and stay updated.
        </p>
      </div>
    </SettingsGroup>
  );
}

function XeroGroup() {
  const [conn, setConn] = useState<XeroConnectionStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  // The code row that last committed — the row the shared save's failure
  // shows on (both codes save as one group, but the blurred row caused it).
  const [codeField, setCodeField] = useState<"revenue" | "payment">("revenue");

  const load = (signal?: AbortSignal) =>
    getXeroStatus(signal)
      .then((res) => {
        setConn(res);
        setLoadError(null);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setLoadError(errorMessage(err));
      });

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Blur commit: both codes save as one group on either row's blur
  // (DESIGN-SYSTEM.md: multi-field groups save the whole group on each field's
  // blur). The draft resyncs when the reloaded connection comes back.
  const codes = useSettingCommit<AccountCodes>(
    {
      revenue: conn?.revenueAccountCode ?? "200",
      payment: conn?.paymentAccountCode ?? "",
    },
    async (next) => {
      await saveXeroSettings({
        revenueAccountCode: next.revenue,
        paymentAccountCode: next.payment,
      });
      await load();
    },
    { equals: codesEqual },
  );

  if (!conn) {
    return (
      <SettingsGroup id="integrations-xero" title="Xero">
        {loadError ? <Banner variant="error">{loadError}</Banner> : <Loading />}
      </SettingsGroup>
    );
  }

  const connect = async () => {
    setConnecting(true);
    setConnectError(null);
    try {
      const { authorizeUrl } = await startXeroConnect();
      window.location.assign(authorizeUrl);
      // `connecting` stays true on success: the browser is navigating away.
    } catch (err) {
      setConnectError(errorMessage(err));
      setConnecting(false);
    }
  };

  const doDisconnect = async () => {
    setDisconnecting(true);
    setDisconnectError(null);
    try {
      await disconnectXero();
      setConfirmDisconnect(false);
      await load();
    } catch (err) {
      setDisconnectError(errorMessage(err));
    } finally {
      setDisconnecting(false);
    }
  };

  const connected = conn.status === "active";
  const codeInputProps = (field: "revenue" | "payment") => ({
    type: "text",
    className: cx(forms.input, chrome.shortField),
    value: codes.draft[field],
    disabled: codes.saving,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      codes.setDraft({ ...codes.draft, [field]: e.target.value }),
    onFocus: codes.onFocus,
    onBlur: () => {
      setCodeField(field);
      void codes.commitDraft();
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") (e.target as HTMLInputElement).blur();
    },
  });

  return (
    <SettingsGroup id="integrations-xero" title="Xero">
      {loadError && (
        <Banner
          variant="error"
          className={chrome.banner}
          onDismiss={() => setLoadError(null)}
        >
          {loadError}
        </Banner>
      )}

      {conn.status === "broken" && (
        <Banner variant="error" className={chrome.banner}>
          The Xero connection stopped working
          {conn.lastError ? ` (${conn.lastError})` : ""}. Reconnect to resume
          syncing —{" "}
          {conn.parkedJobs
            ? `${conn.parkedJobs} queued sync ${conn.parkedJobs === 1 ? "job" : "jobs"}`
            : "queued invoices"}{" "}
          will push once reconnected.
        </Banner>
      )}

      {/* Resolved by action: clears once the failed jobs are dealt with
          (re-send the document or fix the account settings). */}
      {(conn.failedJobs ?? 0) > 0 && (
        <Banner variant="warning" className={chrome.banner}>
          {conn.failedJobs} Xero sync {conn.failedJobs === 1 ? "job" : "jobs"}{" "}
          failed permanently — the invoice or payment never reached Xero. Check
          the invoice in Xero and re-send it from its event if needed.
        </Banner>
      )}

      {/* Setting row: title/description left, status + action right */}
      <div className={styles.connectionRow}>
        <div className={chrome.rowText}>
          <p className={chrome.rowTitle}>Connection</p>
          <p className={chrome.rowMetaTight}>
            {connected
              ? `Connected to ${conn.tenantName}`
              : "Push invoices to Xero and pull reconciled payments back."}
          </p>
          {connectError && (
            <InlineBanner variant="error">{connectError}</InlineBanner>
          )}
        </div>
        <div className={chrome.rowActionsWide}>
          {connected ? (
            <>
              <span className={cx(badge.badge, badge.green)}>
                Connected
              </span>
              <Button
                size="sm"
                variant="danger"
                onClick={() => setConfirmDisconnect(true)}
              >
                Disconnect
              </Button>
            </>
          ) : (
            <>
              <span className={cx(badge.badge, badge.yellow)}>
                {conn.status === "broken" ? "Needs reconnect" : "Not connected"}
              </span>
              <Button size="sm" loading={connecting} onClick={connect}>
                {conn.status === "broken" ? "Reconnect" : "Connect"}
              </Button>
            </>
          )}
        </div>
      </div>

      {connected && (
        /* Dependent settings: only meaningful while connected */
        <>
          <SettingRow
            title="Revenue account code"
            tooltip="The Xero sales account invoice lines post to (200 is Xero's default Sales account)."
            htmlFor="xero-revenue-code"
            error={codeField === "revenue" ? codes.error : null}
            indent
          >
            <input id="xero-revenue-code" {...codeInputProps("revenue")} />
          </SettingRow>
          <SettingRow
            title="Payment account code"
            optional
            tooltip="The Xero bank account a payment you confirm manually here is recorded against."
            htmlFor="xero-payment-code"
            error={codeField === "payment" ? codes.error : null}
            indent
          >
            <input id="xero-payment-code" {...codeInputProps("payment")} />
          </SettingRow>
        </>
      )}

      <ConfirmModal
        open={confirmDisconnect}
        title="Disconnect Xero?"
        message="Invoices will stop syncing and payment status will no longer update from Xero. Reconnecting later starts fresh."
        confirmLabel="Disconnect"
        confirmVariant="danger"
        error={disconnectError}
        loading={disconnecting}
        onConfirm={doDisconnect}
        onCancel={() => {
          setConfirmDisconnect(false);
          setDisconnectError(null);
        }}
      />
    </SettingsGroup>
  );
}
